import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild
} from "@angular/core";
import { Industry, Tenant } from "../../models/tenant.model";
import { MatDialog } from "@angular/material/dialog";
import { EditTenantsComponent } from "../master-tenants/edit-tenants/edit-tenants.component";
import { MasterTenantsService } from "../../services/master-tenants.service";
import { debounceTime } from "rxjs/operators";
import { Subject } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { I18nService } from "../../services";
import { environment } from "../../../../environments/environment";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";

interface YearMonthSelection {
  year: number;
  months: number[];
}

interface DateFilterMonth {
  value: number;
  label: string;
  checked: boolean;
}

interface DateFilterNode {
  year: number;
  expanded: boolean;
  checked: boolean;
  indeterminate: boolean;
  months: DateFilterMonth[];
}

interface TenantDateFiltersResponse {
  createdDates: YearMonthSelection[];
  updatedDates: YearMonthSelection[];
}

type FilterType = "created" | "updated";

@Component({
  selector: "app-master-tenants",
  templateUrl: "./master-tenants.component.html",
  styleUrls: ["./master-tenants.component.scss"]
})
export class MasterTenantsComponent implements OnInit {
  @ViewChild("createdFilterRef") createdFilterRef: ElementRef<HTMLDivElement>;
  @ViewChild("updatedFilterRef") updatedFilterRef: ElementRef<HTMLDivElement>;

  tenants: Tenant[] = [];
  filterName = null;
  filterIndustry: number;
  filterCountry: string;
  filterStatus: boolean;
  filterActiveStatus: string;
  filterUpdateDate: string;
  filterContact: boolean;
  filterParentCorporationValue: string;
  private filterSubject: Subject<string> = new Subject<string>();

  createdDateFilterNodes: DateFilterNode[] = [];
  updatedDateFilterNodes: DateFilterNode[] = [];
  selectedCreatedDateFilters: YearMonthSelection[] = [];
  selectedUpdatedDateFilters: YearMonthSelection[] = [];

  industries: Industry[] = [];
  countries = [];
  industryRetails = [];

  page = 1;
  perPage = 100;
  total = 0;

  currentCode: string;
  parentCorporationList = ["ALL", "OFFICE_INDUSTRIAL", "RETAIL"];

  showDropdown = false;
  showDropdownModifiedDate = false;

  filterDateOptions = {
    year: null,
    thisQuarter: false,
    lastQuarter: false
  };

  filterModifiedDateOptions = {
    yearModifiedDate: null,
    thisQuarterModifiedDate: false,
    lastQuarterModifiedDate: false
  };

  constructor(
    private dialog: MatDialog,
    private masterTenantsService: MasterTenantsService,
    private snackBar: MatSnackBar,
    private i18: I18nService,
    private translate: TranslateService
  ) {
    this.currentCode = environment.platformId;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (
      this.createdFilterRef &&
      !this.createdFilterRef.nativeElement.contains(target)
    ) {
      this.showDropdown = false;
    }

    if (
      this.updatedFilterRef &&
      !this.updatedFilterRef.nativeElement.contains(target)
    ) {
      this.showDropdownModifiedDate = false;
    }
  }

  ngOnInit(): void {
    this.filterSubject.pipe(debounceTime(1000)).subscribe(() => {
      this.onFilterChange();
    });
    this.loadTenants();
    this.getIndustries();
    this.getCountries();
    this.getIndustryReatils();
    this.loadTenantDateFilters();
  }

  loadTenants(): void {
    this.masterTenantsService
      .getMasterTenants(
        this.page - 1,
        this.perPage,
        this.filterName ? this.filterName : null,
        this.filterIndustry,
        this.filterCountry,
        null,
        this.filterStatus,
        this.filterContact,
        this.filterParentCorporationValue,
        this.filterDateOptions,
        this.filterActiveStatus,
        this.filterUpdateDate,
        this.filterModifiedDateOptions,
        this.selectedCreatedDateFilters,
        this.selectedUpdatedDateFilters
      )
      .then((data: any) => {
        this.tenants = data.content;
        this.total = data.totalElements;
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
  }

  getIndustries() {
    this.masterTenantsService.getIndustries().then((resp: Industry[]) => {
      this.industries = resp;
    });
  }

  getIndustryReatils() {
    this.masterTenantsService.getIndustryReatil().then((resp: Industry[]) => {
      this.industryRetails = resp;
    });
  }

  getCountries() {
    this.masterTenantsService.getCountries().then((resp: any) => {
      this.countries = resp;
    });
  }

  loadTenantDateFilters(): void {
    this.masterTenantsService
      .getTenantDateFilters()
      .then((response: TenantDateFiltersResponse) => {
        this.createdDateFilterNodes = this.buildDateFilterNodes(
          response?.createdDates || []
        );
        this.updatedDateFilterNodes = this.buildDateFilterNodes(
          response?.updatedDates || []
        );
      })
      .catch(() => {
        this.createdDateFilterNodes = [];
        this.updatedDateFilterNodes = [];
      });
  }

  editTenant(tenant) {
    const dialogRef = this.dialog.open(EditTenantsComponent, {
      width: "1000px",
      data: {
        tenant: tenant,
        create: false,
        industries: this.industries,
        countries: this.countries,
        industryRetails: this.industryRetails
      }
    });

    dialogRef.afterClosed().subscribe((updatedTenant: Tenant) => {
      if (updatedTenant && updatedTenant.action === "saveTenant") {
        updatedTenant.name = updatedTenant.name.trim();
        this.masterTenantsService
          .updateTenant(updatedTenant)
          .then((response: any) => {
            if (response?.error) {
              this.showMessage("master-tenant.errorName");
            } else {
              this.showMessage("master-tenant.updateMessage");
              this.loadTenants();
            }
          });
      } else if (
        updatedTenant &&
        updatedTenant.action === "Toggle Activation"
      ) {
        this.showMessage("master-tenant.updateMessage");
        this.loadTenants();
      } else if (
        updatedTenant &&
        updatedTenant.action === "error deactivating"
      ) {
        this.showMessage("master-tenant.errorDeactivating");
      } else if (updatedTenant && updatedTenant.action === "mergeCorporation") {
        this.loadTenants();
      }
    });
  }

  openAddDialog() {
    let newTenant = new Tenant();
    const dialogRef = this.dialog.open(EditTenantsComponent, {
      width: "1000px",
      data: {
        tenant: newTenant,
        create: true,
        industries: this.industries,
        countries: this.countries,
        industryRetails: this.industryRetails
      }
    });

    dialogRef.afterClosed().subscribe((newTenant: Tenant) => {
      if (newTenant) {
        newTenant.name = newTenant.name.trim();
        this.masterTenantsService
          .createTenant(newTenant)
          .then((response: any) => {
            if (response?.error) {
              this.showMessage("master-tenant.errorName");
            } else {
              this.showMessage("master-tenant.createMessage");
              this.loadTenants();
            }
          });
      }
    });
  }
  onFilterChange(): void {
    this.loadTenants();
  }

  onInputChange() {
    this.filterSubject.next();
  }

  toggleDropdown(type: FilterType, event: MouseEvent): void {
    event.stopPropagation();
    if (type === "created") {
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) {
        this.showDropdownModifiedDate = false;
      }
    } else {
      this.showDropdownModifiedDate = !this.showDropdownModifiedDate;
      if (this.showDropdownModifiedDate) {
        this.showDropdown = false;
      }
    }
  }

  toggleYearExpansion(node: DateFilterNode, event: MouseEvent): void {
    event.stopPropagation();
    node.expanded = !node.expanded;
  }

  onYearSelectionChange(
    type: FilterType,
    node: DateFilterNode,
    event: Event
  ): void {
    event.stopPropagation();
    const checked = (event.target as HTMLInputElement).checked;
    node.checked = checked;
    node.indeterminate = false;
    node.months.forEach(month => (month.checked = checked));
    this.syncDateSelections(type);
  }

  onMonthSelectionChange(
    type: FilterType,
    node: DateFilterNode,
    month: DateFilterMonth,
    event: Event
  ): void {
    event.stopPropagation();
    month.checked = (event.target as HTMLInputElement).checked;
    const selectedMonths = node.months.filter(item => item.checked).length;
    if (selectedMonths === 0) {
      node.checked = false;
      node.indeterminate = false;
    } else if (selectedMonths === node.months.length) {
      node.checked = true;
      node.indeterminate = false;
    } else {
      node.checked = false;
      node.indeterminate = true;
    }
    this.syncDateSelections(type);
  }

  getDateFilterSummary(type: FilterType): string {
    const selection =
      type === "created"
        ? this.selectedCreatedDateFilters
        : this.selectedUpdatedDateFilters;

    if (!selection.length) {
      return this.translate.instant("master-tenant.filterByDate");
    }

    const parts = selection.map(item => {
      if (!item.months || item.months.length === 0) {
        return item.year.toString();
      }
      const months = item.months
        .slice()
        .sort((a, b) => a - b)
        .map(month => this.getMonthLabel(month).slice(0, 3))
        .join(", ");
      return `${item.year} (${months})`;
    });

    return parts.join(", ");
  }

  private syncDateSelections(type: FilterType): void {
    if (type === "created") {
      this.selectedCreatedDateFilters = this.extractSelections(
        this.createdDateFilterNodes
      );
    } else {
      this.selectedUpdatedDateFilters = this.extractSelections(
        this.updatedDateFilterNodes
      );
    }
    this.onFilterChange();
  }

  private extractSelections(nodes: DateFilterNode[]): YearMonthSelection[] {
    return nodes
      .map(node => {
        const selectedMonths = node.months
          .filter(month => month.checked)
          .map(month => month.value);
        if (node.checked || selectedMonths.length) {
          const includeAllMonths =
            node.checked && selectedMonths.length === node.months.length;
          return {
            year: node.year,
            months: includeAllMonths ? [] : selectedMonths
          };
        }
        return null;
      })
      .filter(Boolean) as YearMonthSelection[];
  }

  private buildDateFilterNodes(data: YearMonthSelection[]): DateFilterNode[] {
    return data.map(item => ({
      year: item.year,
      expanded: false,
      checked: false,
      indeterminate: false,
      months: (item.months || [])
        .slice()
        .sort((a, b) => a - b)
        .map(value => ({
          value,
          label: this.getMonthLabel(value),
          checked: false
        }))
    }));
  }

  private getMonthLabel(month: number): string {
    const locale = this.mapMomentLocale(
      this.translate.currentLang || this.translate.getDefaultLang() || "en"
    );
    return moment()
      .locale(locale)
      .month(month - 1)
      .format("MMMM");
  }

  private mapMomentLocale(lang: string): string {
    if (!lang) {
      return "en";
    }
    const normalized = lang.toLowerCase();
    if (normalized.startsWith("pt")) {
      return "pt-br";
    }
    if (normalized.startsWith("es")) {
      return "es";
    }
    if (normalized.startsWith("en")) {
      return "en";
    }
    return normalized;
  }

  setUrl(url) {
    if (!/^https?:\/\//i.test(url)) {
      url = "https://" + url;
    }
    return url;
  }

  showMessage(message) {
    this.snackBar.open(this.i18.get(message), "Close", {
      duration: 3000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: ["custom-snackbar"]
    });
  }

  onPageChange(event) {
    this.page = event;
    this.loadTenants();
  }

  validateContact(tenant) {
    return (
      (tenant.contactName !== null && tenant.contactName !== "") ||
      (tenant.contactEmail !== null && tenant.contactEmail !== "") ||
      (tenant.contactPhone !== null && tenant.contactPhone !== "")
    );
  }
}
